import ScrollReveal from "@/components/base/ScrollReveal";
import { Link } from "react-router-dom";

export default function EngagementModelSection() {
  const supportTypes = [
    "Advisory guidance",
    "Tailored, targeted recommendations",
    "Vector checks",
    "Quality reviews",
    "Team kick-start support",
    "Focused content inputs to strategies, plans, processes, policies, etc.",
  ];

  return (
    <section id="engagement-model" className="relative bg-bg py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/3 -left-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -right-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      {/* Dot texture */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
        backgroundSize: "40px 40px",
      }} />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Engagement Model
              </span>
              <h2 className="font-serif text-[32px] md:text-[44px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                How <span className="italic text-primary">VantageSight</span> Can Support You
              </h2>
              <p className="font-sans text-[17px] md:text-[19px] text-text-muted leading-relaxed max-w-3xl mx-auto">
                Engagements can focus on a range of topics, such as a full AI strategy and governance approach, targeted support for a specific phase of the AI journey, or a specific AI policy.
              </p>
            </div>
          </ScrollReveal>

          {/* Leadership presentation image */}
          <ScrollReveal delay={100}>
            <div className="mb-14">
              <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-bg-card">
                <img
                  src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/1f5a9a0c18a73704c27c78d286629ade.png"
                  alt="Three professional women collaborating over laptop and documents at round table in modern workspace"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <ScrollReveal direction="left">
              <div className="bg-bg-card rounded-2xl p-8 md:p-10 border border-border relative overflow-hidden h-full">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary-tint rounded-full flex items-center justify-center">
                      <i className="ri-focus-3-line text-primary text-xl w-6 h-6 flex items-center justify-center" />
                    </div>
                    <span className="font-sans text-[14px] font-semibold text-primary tracking-wider uppercase">
                      Approach
                    </span>
                  </div>
                  <p className="font-sans text-[16px] text-text-muted leading-[1.7]">
                    VantageSight complements the skills and abilities of your organization's teams by leveraging deep expertise and experience to point teams in the right direction and keep them on-track. VantageSight provides targeted guidance, advice, direction, recommendations, and focus, as well as reviews of and inputs to work products. Your teams maintain responsibility for work products and outcomes.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={150}>
              <div className="h-full">
                <h4 className="font-sans text-[14px] font-semibold text-text-headline uppercase tracking-wider mb-5">
                  Support Modes – How VantageSight Expertise and Experience Are Delivered
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                  {supportTypes.map((type, i) => (
                    <ScrollReveal key={i} delay={200 + i * 60} direction="right">
                      <div className="flex items-center gap-3 bg-bg-card rounded-xl px-4 py-3 border border-border transition-all duration-300 hover:border-primary/30 group">
                        <div className="w-6 h-6 bg-primary-tint rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                          <i className="ri-check-line text-primary text-sm w-4 h-4 flex items-center justify-center" />
                        </div>
                        <span className="font-sans text-[14px] text-text-body">
                          {type}
                        </span>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={400}>
            <div className="text-center mt-14">
              <Link
                to="/contact"
                className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                Discuss How VantageSight Can Help
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}